import { useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { inputCls } from "@/lib/billing";
import { Btn, Card } from "@/components/PortalPage";

const List = ({ title, items, testId }) => items?.length ? <div data-testid={testId}><p className="text-[10px] font-bold tracking-[1px] text-[#888]">{title}</p><ul className="list-disc pl-4 text-[12px]">{items.map((x, i) => <li key={i}>{typeof x === "string" ? x : `${x.text}${x.date ? ` (${x.date})` : ""}`}</li>)}</ul></div> : null;

export const DraftCard = ({ d, onDone }) => {
  const [f, setF] = useState({ channel: d.channel, subject: d.subject, body: d.body });
  const [busy, setBusy] = useState("");
  const act = async (what) => {
    setBusy(what);
    try {
      if (what === "send") { await api(`/comms/drafts/${d.id}/approve`, { method: "POST", body: f }); toast.success("Approved and sent"); }
      else { await api(`/comms/drafts/${d.id}/discard`, { method: "POST" }); toast.success("Discarded"); }
      onDone();
    } catch (e) { toast.error(e.message); onDone(); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-2 rounded-xl border border-[#A78BFA] bg-[#1A1526] p-3" data-testid={`draft-${d.id}`}>
      <p className="flex items-center gap-1 text-[10px] font-extrabold tracking-[1px] text-[#A78BFA]"><Sparkles size={11} />AI DRAFT · REVIEW BEFORE SENDING · nothing goes out until you approve</p>
      <select className={inputCls} value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })} data-testid={`draft-channel-${d.id}`}><option value="email">Email</option><option value="sms">Text</option></select>
      {f.channel === "email" && <input className={inputCls} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid={`draft-subject-${d.id}`} />}
      <textarea className={inputCls} rows={5} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid={`draft-body-${d.id}`} />
      <div className="flex gap-2"><Btn onClick={() => act("send")} busy={busy === "send"} data-testid={`draft-approve-${d.id}`}>APPROVE & SEND</Btn><Btn variant="outline" onClick={() => act("discard")} busy={busy === "discard"} data-testid={`draft-discard-${d.id}`}>DISCARD</Btn></div>
    </div>
  );
};

export const AiPanel = ({ customer, opp, drafts, onChange }) => {
  const [busy, setBusy] = useState("");
  const [ins, setIns] = useState(null);
  const run = async (task) => {
    setBusy(task);
    try { const r = await api(`/comms/customers/${customer.id}/ai`, { method: "POST", body: { task } }); if (task === "insights") setIns(r); else onChange(); }
    catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  const useDate = async () => {
    try { await api(`/comms/opportunities/${opp.id}`, { method: "PUT", body: { next_action: ins.suggested_next_action, due_date: ins.suggested_due_date } }); toast.success("Next action updated"); onChange(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <Card className="space-y-3" data-testid="comm-ai">
      <p className="flex items-center gap-2 text-xs font-extrabold tracking-[1.5px] text-[#A78BFA]"><Sparkles size={14} />AI ASSISTANT</p>
      <div className="flex flex-wrap gap-2">
        <Btn variant="outline" onClick={() => run("insights")} busy={busy === "insights"} data-testid="ai-insights-btn">SUMMARIZE & CHECK</Btn>
        <Btn variant="outline" onClick={() => run("followup")} busy={busy === "followup"} data-testid="ai-followup-btn">DRAFT QUOTE FOLLOW-UP</Btn>
        <Btn variant="outline" onClick={() => run("confirmation")} busy={busy === "confirmation"} data-testid="ai-confirm-btn">DRAFT APPOINTMENT CONFIRMATION</Btn>
      </div>
      {ins && (
        <div className="space-y-2 rounded-lg bg-[#121212] p-3" data-testid="ai-insights">
          <p className="text-[10px] text-[#A78BFA]">{ins.caution}</p>
          <p className="text-[12px]">{ins.summary}</p>
          <List title="UNANSWERED QUESTIONS" items={ins.unanswered} testId="ai-unanswered" />
          <List title="REQUESTED CHANGES" items={ins.change_requests} testId="ai-changes" />
          <List title="CUSTOMER COMMITMENTS" items={ins.commitments} testId="ai-commitments" />
          {ins.stalled && <p className="text-[11px] font-bold text-[#EF4444]">Looks stalled.</p>}
          <p className="text-[12px]"><b>Suggested next step:</b> {ins.suggested_next_action} {ins.suggested_due_date && `by ${ins.suggested_due_date}`}</p>
          {opp && ins.suggested_due_date && <Btn variant="outline" onClick={useDate} data-testid="ai-use-suggestion">USE THIS AS THE NEXT ACTION</Btn>}
        </div>
      )}
      {drafts.map((d) => <DraftCard key={d.id} d={d} onDone={onChange} />)}
    </Card>
  );
};
